import React, { useEffect, useRef, useState } from "react";
import { Bot, X, Send } from "lucide-react";
import { useI18n } from "../i18n";
import "./ChatWidget.css";

const API = process.env.REACT_APP_BACKEND_URL;

function getSessionId() {
  let id = localStorage.getItem("altamira_chat_session");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("altamira_chat_session", id);
  }
  return id;
}

export default function ChatWidget() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/chat/${sessionId.current}`)
      .then((r) => r.json())
      .then((docs) => { if (Array.isArray(docs) && docs.length) setMessages(docs.map((d) => ({ role: d.role, content: d.content }))); })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, lang }),
      });
      if (!res.ok) throw new Error("bad status");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = JSON.parse(part.slice(6));
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
              return copy;
            });
          } else if (data.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: t({ es: "Lo siento, ocurrió un error. Intente de nuevo o escríbanos por WhatsApp.", en: "Sorry, an error occurred. Try again or message us on WhatsApp." }) };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t({ es: "No se pudo conectar. Intente de nuevo.", en: "Could not connect. Please try again." }) };
        return copy;
      });
    }
    setBusy(false);
  };

  return (
    <>
      <button className="chatfab" data-testid="chat-widget-button" onClick={() => setOpen((o) => !o)} aria-label="Chat">
        {open ? <X size={20} /> : <Bot size={20} />}
        {!open && <span>{t({ es: "Asistente virtual", en: "Virtual assistant" })}</span>}
      </button>
      {open && (
        <div className="chatpanel" data-testid="chat-widget-panel">
          <div className="chathead">
            <div className="chathead-ico"><Bot size={18} /></div>
            <div>
              <b>{t({ es: "Asistente Altamira", en: "Altamira Assistant" })}</b>
              <small>{t({ es: "Impulsado por IA · ES/EN", en: "AI powered · ES/EN" })}</small>
            </div>
          </div>
          <div className="chatbody" ref={bodyRef} data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="chatmsg assistant" data-testid="chat-welcome-message">
                {t({
                  es: "¡Hola! Soy el asistente virtual de Grupo Inversiones Altamira. Puedo ayudarle con equipos de bombeo, válvulas, transformadores, EPP y más. ¿En qué puedo ayudarle?",
                  en: "Hello! I'm the Grupo Inversiones Altamira virtual assistant. I can help you with pumping equipment, valves, transformers, PPE and more. How can I help you?",
                })}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`chatmsg ${m.role}`} data-testid={`chat-message-${m.role}`}>
                {m.content || <span className="typing"><i /><i /><i /></span>}
              </div>
            ))}
          </div>
          <div className="chatfoot">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder={t({ es: "Escriba su pregunta...", en: "Type your question..." })}
              disabled={busy}
            />
            <button data-testid="chat-send-button" onClick={send} disabled={busy || !input.trim()} aria-label="Send">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
